import { ETIQUETA_DIA, findExercise, isMinor, MENSAJE_FUERA_DE_ALCANCE } from '@cadencia/domain';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Boton, BotonEnlace, Cabecera, Chip, Estado, Seccion, Tarjeta } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { leerBloqueActivo, useBloqueActivo } from '../../data/bloqueActivo.js';
import { situacionDelTest, usePerfil } from '../../data/perfilConocido.js';
import { crearPrimerBloque } from '../../data/services/testInicialService.js';
import { getDefaultGym, listEquipment } from '../../data/repositories/gymRepo.js';
import {
  createBlockForPhase,
  createReadaptationBlock,
  ensureWeekMaterialized,
  listSessionsOfWeek,
  listWeeks,
} from '../../data/repositories/trainingRepo.js';
import { getActiveWorkout } from '../../data/repositories/workoutRepo.js';
import { reviewBlockEnd } from '../../data/services/blockChainService.js';
import { prepareNextWeekIfFinished } from '../../data/services/weekCloseService.js';
import { CerrarSemana } from './CerrarSemana.js';
import { CuandoEmpezar, lunesDeArranque, lunesPorDefecto } from './CuandoEmpezar.js';
import { CambiarInicio } from './CambiarInicio.js';
import { blockHasLoggedWork } from '../../data/services/rescheduleBlockService.js';
import {
  proponerBloque,
  type PropuestaDeBloque,
} from '../../data/services/blockProposalService.js';
import { useCoachAI } from '../../data/useCoachAI.js';
import { formatShortDate, todayLocal } from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import './entrenar.css';
import { detalleDeError, mensajeDeError } from '../../lib/errores.js';

/** El nombre en castellano del catalogo; el `slug` no se enseña nunca. */
function nombreDeEjercicio(slug: string): string {
  return findExercise(slug)?.nameEs ?? slug;
}

export function EntrenarPage(): ReactNode {
  const { ctx, ready, refreshPending } = useAppData();
  const navigate = useNavigate();
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [diseno, setDiseno] = useState<PropuestaDeBloque | null>(null);
  // Si se pidió a la IA y no diseñó el bloque, por qué. Va aparte de `avisos`,
  // que hablan de huecos de equipamiento y mandarían a otra pantalla (R-24).
  const [motivoSinIA, setMotivoSinIA] = useState<string | null>(null);
  // Lo que ha pasado al cerrar la semana. Vive aquí y no en `CerrarSemana`: al
  // cerrar, la semana activa pasa a ser la siguiente y ese control desaparece.
  const [avisoSemana, setAvisoSemana] = useState<string | null>(null);
  // El lunes de ESTA semana, que es lo que `lunesPorDefecto` devuelve y lo que
  // esta pantalla hacia ya. El comentario decia «el proximo lunes» y era falso
  // desde que se escribio: quien fija el defecto es `lunesPorDefecto`, cuya
  // cabecera explica por que se mantiene el de esta semana —empezar hoy es
  // legitimo y esperar seis dias, no— y que lo que se anadio fue el AVISO de
  // `CuandoEmpezar`, con el atajo al lunes siguiente.
  const [inicio, setInicio] = useState(() => lunesPorDefecto());
  const ia = useCoachAI();

  const userId = ctx?.userId;

  const equipamiento = useLiveQuery(async () => {
    if (!userId) return [];
    const gym = await getDefaultGym(userId);
    return gym ? listEquipment(gym.id) : [];
  }, [userId]);

  /*
   * `bloque` y «¿se sabe ya?» salen de la MISMA consulta (`bloqueActivo.ts`).
   * Con Dexie vacía y la primera bajada en vuelo, «no hay bloque» no significa
   * que no lo haya: significa que no se ha mirado, y ofrecer crear uno ahí
   * duplica el plan y paga una propuesta de IA que sobra.
   */
  const estadoBloque = useBloqueActivo(userId ?? null);
  const bloque = estadoBloque?.bloque ?? undefined;
  // Qué sabe la aplicación del atleta: sin perfil, se empieza por el test.
  const estadoPerfil = usePerfil(userId ?? null);
  const situacion = situacionDelTest(estadoPerfil);
  const perfil = estadoPerfil?.perfil ?? null;

  const semanas = useLiveQuery(async () => (bloque ? listWeeks(bloque.id) : []), [bloque?.id]) ?? [];

  const semanaActiva = semanas.find((semana) => semana.status === 'active') ?? semanas[0];

  const sesiones =
    useLiveQuery(
      async () => (semanaActiva ? listSessionsOfWeek(semanaActiva.id) : []),
      [semanaActiva?.id],
    ) ?? [];

  const enCurso = useLiveQuery(
    async () => (userId ? getActiveWorkout(userId) : undefined),
    [userId],
  );

  /**
   * ¿Se puede todavia mover el bloque de fecha?
   *
   * Solo mientras no haya entrenamiento registrado dentro. Se consulta aqui —y
   * no dentro del control— porque el que decide es el dominio y la respuesta
   * cambia en cuanto se empieza una sesion: en vivo, como todo lo demas de esta
   * pantalla.
   */
  const bloqueSinEntrenar =
    useLiveQuery(
      async () => (userId && bloque ? !(await blockHasLoggedWork(userId, bloque.id)) : false),
      [userId, bloque?.id],
    ) ?? false;

  /**
   * Crea las sesiones de la semana en curso si todavia no existen.
   *
   * Las filas de `training_weeks` se crean todas al empezar el bloque, pero las
   * sesiones solo se materializaban las de la primera semana: al terminarla,
   * esta pantalla se quedaba vacia. Se hace aqui, al entrar a entrenar, y no en
   * un proceso de fondo, porque es el momento en que hacen falta.
   */
  const bloqueId = bloque?.id;
  /*
   * Candado de reentrada, y no es teorico.
   *
   * Desde que `reviewBlockEnd` puede pedirle el bloque a la IA, este efecto
   * ESPERA A LA RED —segundos, no milisegundos—. Y `ia` esta en las
   * dependencias porque el efecto lo usa: `useCoachAI` devuelve un proveedor
   * distinto cuando la preferencia pasa de «no la he mirado» a «encendida», que
   * es justo lo que ocurre cuando termina la primera bajada.
   *
   * Sin candado, esas dos cosas juntas dan una carrera: la primera ejecucion
   * sigue esperando al modelo, la segunda entra, las dos ven un bloque agotado
   * —porque ninguna lo ha cerrado todavia— y las dos encadenan. El usuario
   * acaba con dos bloques activos y dos planes el mismo dia.
   *
   * Un `ref` y no un estado: cambiarlo no tiene que repintar nada, y un estado
   * volveria a disparar el efecto.
   */
  const preparando = useRef(false);
  useEffect(() => {
    if (!ctx || bloqueId === undefined) return;
    if (preparando.current) return;
    preparando.current = true;
    void (async () => {
      try {
        // Primero se mira si el bloque se ha agotado: si hay que encadenar otro,
        // materializar una semana del que ya termino no sirve de nada.
        // La IA entra tambien AQUI, que es el camino por el que nacen casi
        // todos los bloques de la vida de un usuario: cuando el anterior se
        // agota y el objetivo no ha cambiado. Con el proveedor apagado o sin
        // red, `reviewBlockEnd` hace exactamente lo que hacia antes.
        await reviewBlockEnd(ctx, todayLocal(), { ia });
        await ensureWeekMaterialized(ctx, todayLocal());
        // Con la semana ya sin nada pendiente, la siguiente se prepara aquí y no
        // el lunes: un domingo con todo hecho no tiene por qué esperar
        // (13/09/2026, `weekCloseService`).
        await prepareNextWeekIfFinished(ctx, todayLocal());
      } catch (caught) {
        // Sin esto la excepcion se perdia en una promesa sin dueño: la semana
        // no aparecia y la pantalla no daba ninguna pista de por que. Un fallo
        // silencioso aqui es peor que uno ruidoso.
        logger.error('engine', 'WEEK_SETUP_FAILED', 'No se pudo preparar la semana', {
          message: detalleDeError(caught),
        });
        setError(mensajeDeError(caught));
      } finally {
        preparando.current = false;
      }
    })();
  }, [ctx, bloqueId, ia]);

  if (!ready || !ctx) return <Estado tipo="cargando" titulo="Cargando entrenamiento" />;

  const hoy = todayLocal();
  const sesionDeHoy = sesiones.find((sesion) => sesion.scheduledDate === hoy);
  const siguiente = sesiones.find(
    (sesion) => sesion.scheduledDate >= hoy && sesion.status === 'planned',
  );
  /**
   * Sesiones de esta semana que se quedaron atras sin hacer (R-20).
   *
   * `siguiente` solo mira de hoy en adelante, asi que un bloque creado un
   * sabado —con sus cuatro sesiones ya en el pasado— no tenia ni «hoy» ni
   * «proxima» y la tarjeta caia en «Hoy toca descansar · No hay sesion
   * programada», con las cuatro listadas justo debajo como pendientes. Son dos
   * situaciones distintas metidas en el mismo mensaje: no es que no haya sesion
   * programada, es que vas por detras.
   */
  const atrasadas = sesiones.filter(
    (sesion) => sesion.scheduledDate < hoy && sesion.status === 'planned',
  );

  /**
   * Crea el primer bloque. Con `conIA`, se lo DISEÑA el modelo.
   *
   * QUE CAMBIO EL 22/09/2026
   *
   * Antes la IA solo elegia que ejercicio iba en cada hueco de una plantilla
   * que ya estaba escrita: cuatro sesiones torso/pierna, 27 huecos, roles y
   * orden fijos. Ahora propone las SESIONES —cuantas, que entrena cada una y
   * con que— ademas de las semanas y el volumen. Es lo que el §7 del encargo
   * exige de «toda programacion inicial».
   *
   * SIGUEN SIENDO DOS BOTONES, y no uno con interruptor, porque **cuestan
   * cosas distintas**: la determinista es instantanea y gratis; la otra son
   * unos segundos de espera y unos centimos. Meter eso detras del boton de
   * siempre convertiria «crear un bloque» en algo que a veces tarda sin que el
   * usuario haya pedido nada.
   *
   * LO QUE LA IA NO DECIDE, se pida o no: la rampa entre semanas —cuantas
   * series y con que RIR se progresa, donde cae el deload— la pone
   * `PHASE_RAMPS`, porque es progresion y el §7 se la asigna al motor local. Y
   * el orden dentro de la sesion lo impone `ordenarSesion`: los basicos
   * primero, siempre.
   *
   * SI LA IA NO CONTESTA o su estructura no pasa las reglas, sale el bloque
   * determinista y **se dice** (§13: los respaldos son identificables).
   */
  const crearBloque = (conIA: boolean): void => {
    setCreando(true);
    setError(null);
    setDiseno(null);
    setMotivoSinIA(null);
    void (async () => {
      try {
        // La fecha elegida va en las DOS rutas. Que solo la respetara una
        // significaria que pedirle los ejercicios a la IA te cambia ademas el
        // dia de empezar, que no es lo que se ha pulsado.
        const startDate = lunesDeArranque(inicio);

        if (!conIA) {
          const resultado = await createReadaptationBlock(ctx, { startDate });
          setAvisos(resultado.warnings);
        } else {
          const { propuesta, motivo } = await proponerBloque(ia, ctx.userId, 'readaptation');
          // Si hubo propuesta pero su estructura se cayó, lo dice ya la sección
          // «Cómo se ha diseñado este bloque».
          setMotivoSinIA(propuesta === null ? motivo : null);
          // Si mientras pensaba la IA bajó el bloque de otro dispositivo, no se
          // crea un segundo: la pantalla se repinta con el que ya hay.
          const despues = await leerBloqueActivo(ctx.userId);
          if (!despues.conocido || despues.bloque !== null) {
            await refreshPending();
            return;
          }
          const resultado = await createBlockForPhase(ctx, 'readaptation', {
            startDate,
            ...(propuesta
              ? {
                  weeks: propuesta.weeks,
                  volumeTargets: propuesta.targets,
                  ...(propuesta.sessionPlan ? { sessionPlan: propuesta.sessionPlan } : {}),
                }
              : {}),
          });
          setAvisos(resultado.warnings);
          setDiseno(propuesta);
        }
        await refreshPending();
      } catch (caught) {
        setError(mensajeDeError(caught));
      } finally {
        setCreando(false);
      }
    })();
  };

  // --- Sin equipamiento: no se puede programar nada realista ----------------
  if ((equipamiento?.length ?? 0) === 0) {
    return (
      <>
        <Cabecera titulo="Entrenar" />
        <Estado
          tipo="primera-vez"
          titulo="Primero, tu gimnasio"
          descripcion="Sin saber qué máquinas y cargas tienes disponibles, cualquier peso que te recomendara sería inventado."
          accion={
            <BotonEnlace a="/mas/gimnasio">Configurar gimnasio</BotonEnlace>
          }
        />
      </>
    );
  }

  // --- Todavia no se sabe si hay bloque: NO se ofrece crear ninguno ---------
  if (estadoBloque === undefined || !estadoBloque.conocido) {
    return (
      <>
        <Cabecera titulo="Entrenar" />
        <Estado
          tipo="cargando"
          titulo="Sincronizando tu plan"
          descripcion="Este dispositivo todavía no ha recibido tus datos. Si ya tienes un bloque en marcha, aparecerá en cuanto llegue."
        />
      </>
    );
  }

  // --- Sin bloque y sin perfil: la aplicación empieza por el test -----------
  // (docs/TEST_INICIAL.md). Crear un bloque aquí sería programar a ciegas: sin
  // días, sin tiempo y sin saber qué articulación no cargar.
  if (!bloque && situacion === 'sin_perfil') {
    return (
      <>
        <Cabecera titulo="Entrenar" />
        <Estado
          tipo="primera-vez"
          titulo="Antes de programar, conozcámonos"
          descripcion="Unas preguntas sobre ti, tu semana y tus molestias. Con eso creo tu primer bloque."
          accion={<BotonEnlace a="/bienvenida">Empezar el test</BotonEnlace>}
        />
      </>
    );
  }

  // --- Sin bloque y menor de edad: no hay plan automático por ningún camino --
  // (safety/population.ts). Se dice aquí, y no como un aviso después de pulsar:
  // la tarjeta de avisos de esta pantalla habla de huecos de equipamiento y
  // mandaría a la pantalla equivocada (R-24).
  if (!bloque && isMinor(perfil?.birthDate ?? null, todayLocal())) {
    return (
      <>
        <Cabecera titulo="Entrenar" />
        <Tarjeta tono="aviso" titulo="Sin plan automático">
          <p>{MENSAJE_FUERA_DE_ALCANCE.minor}</p>
        </Tarjeta>
      </>
    );
  }

  // --- Sin bloque, con el test hecho: su semana, no la de la plantilla -------
  // Pasa si el test se hizo sin gimnasio configurado. Mismo camino que el test
  // (`crearPrimerBloque`), para que la fase salga igual por los dos lados.
  if (!bloque && perfil?.availability) {
    const disponibilidad = perfil.availability;
    const dias = [...disponibilidad.days]
      .sort((a, b) => a - b)
      .map((d) => (ETIQUETA_DIA[d] ?? '').toLowerCase())
      .join(', ');
    const crearDesdeElTest = (conIA: boolean): void => {
      setCreando(true);
      setError(null);
      setDiseno(null);
      setMotivoSinIA(null);
      void (async () => {
        try {
          const resultado = await crearPrimerBloque(ctx, {
            inicio: lunesDeArranque(inicio),
            hoy: todayLocal(),
            ia: conIA ? ia : null,
          });
          setAvisos(resultado.avisos);
          setDiseno(resultado.propuesta);
          setMotivoSinIA(resultado.propuesta === null ? resultado.motivoSinIA : null);
          await refreshPending();
        } catch (caught) {
          setError(mensajeDeError(caught));
        } finally {
          setCreando(false);
        }
      })();
    };
    return (
      <>
        <Cabecera titulo="Entrenar" />
        <Tarjeta
          tono="focal"
          titulo="Tu primer bloque"
          descripcion={`${String(disponibilidad.days.length)} sesiones por semana (${dias}), de hasta ${String(disponibilidad.maxSessionMinutes)} minutos. La fase sale de lo que contaste en el test.`}
          acciones={
            <>
              <Boton onClick={() => crearDesdeElTest(false)} cargando={creando} disabled={inicio === ''}>
                {creando ? 'Creando…' : 'Crear mi primer bloque'}
              </Boton>
              {ia.enabled && (
                <Boton variante="secundario" onClick={() => crearDesdeElTest(true)} disabled={creando}>
                  Que la IA diseñe el bloque
                </Boton>
              )}
            </>
          }
        >
          <CuandoEmpezar valor={inicio} onChange={setInicio} deshabilitado={creando} />
          {error !== null && (
            <p className="campo__error" role="alert">
              {error}
            </p>
          )}
        </Tarjeta>
      </>
    );
  }

  // --- Sin bloque, perfil de antes del test: la readaptación de siempre -------
  // Para ese perfil, «4 sesiones por semana, torso/pierna» sigue siendo verdad:
  // sin disponibilidad declarada, el bloque es el de los cuatro días fijos.
  if (!bloque) {
    return (
      <>
        <Cabecera titulo="Entrenar" />
        <Tarjeta
          tono="focal"
          titulo="Bloque de readaptación"
          descripcion="4 semanas, 4 sesiones por semana, torso/pierna. RIR alto, sin fallo y sin técnicas avanzadas: vuelves después de un tiempo parado."
          acciones={
            <>
              <Boton
                onClick={() => crearBloque(false)}
                cargando={creando}
                disabled={inicio === ''}
              >
                {creando ? 'Creando…' : 'Crear bloque de readaptación'}
              </Boton>
              {ia.enabled && (
                <Boton
                  variante="secundario"
                  onClick={() => crearBloque(true)}
                  disabled={creando}
                >
                  Que la IA elija los ejercicios
                </Boton>
              )}
            </>
          }
        >
          <CuandoEmpezar valor={inicio} onChange={setInicio} deshabilitado={creando} />

          <p className="campo__pista">
            Los ejercicios se eligen con el equipamiento que has declarado. La primera semana se
            crea ahora; las siguientes, cuando lleguen.
          </p>
          {error !== null && (
            <p className="campo__error" role="alert">
              {error}
            </p>
          )}
        </Tarjeta>
      </>
    );
  }

  return (
    <>
      {/*
        El bloque y la semana van ENCIMA del titulo, en versalita: es donde
        estas, no como se llama la pantalla. Debajo competian con «Entrenar»
        estando escritos con la clase de la pista de un campo de formulario.

        La puerta que faltaba: la fecha de inicio se elige al CREAR el bloque y
        esa tarjeta solo sale cuando no hay ninguno activo, asi que un bloque de
        pruebas se quedaba corriendo sin forma de volver atras. Va junto al
        «semana 3 de 4», que es lo que delata el problema.
      */}
      <Cabecera
        titulo="Entrenar"
        encima={`${bloque.name} · semana ${semanaActiva?.weekIndex ?? 1} de ${bloque.plannedWeeks}`}
        acciones={<CambiarInicio bloque={bloque} puedeMoverse={bloqueSinEntrenar} />}
      />

      {avisos.length > 0 && (
        <Tarjeta tono="aviso" titulo="Huecos sin cubrir">
          <ul className="por-que__lista">
            {avisos.map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
          <p className="campo__pista">
            Añade el equipamiento que falte y vuelve a crear el bloque si quieres cubrirlos.
          </p>
        </Tarjeta>
      )}

      {motivoSinIA !== null && (
        <p className="campo__pista" role="status">
          La IA no ha diseñado este bloque ({motivoSinIA}), así que es el de la plantilla del motor.
        </p>
      )}

      {diseno !== null && (
        <Seccion
          titulo="Cómo se ha diseñado este bloque"
          descripcion="La IA propuso la estructura y el volumen dentro de los límites del motor. La progresión entre semanas —series, RIR y descargas— la pone el motor."
        >
          <p className="por-que__texto">{diseno.rationale}</p>

          {diseno.sessionPlan !== null ? (
            diseno.sessionPlan.map((sesion) => (
              <div key={sesion.name} className="por-que__bloque">
                <h3 className="por-que__titulo">{sesion.name}</h3>
                <p className="campo__pista">{sesion.objective}</p>
                <ul className="por-que__lista">
                  {sesion.slots.map((hueco) => (
                    <li key={hueco.candidates[0]}>
                      {nombreDeEjercicio(hueco.candidates[0] ?? '')}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            /*
              El respaldo, DICHO (§13). Que la estructura se haya caído no es lo
              mismo que que la IA no contestara, y las dos cosas son distintas de
              «la IA lo diseñó». Sin esta línea, un bloque de plantilla se vería
              exactamente igual que uno diseñado.
            */
            <p className="campo__pista">
              La estructura que propuso no pasó las reglas del motor, así que las sesiones
              son las de la plantilla habitual. Las semanas y el volumen sí son suyos.
            </p>
          )}
        </Seccion>
      )}

      {avisoSemana !== null && (
        <p className="porque" role="status">
          {avisoSemana}
        </p>
      )}

      {enCurso && enCurso.sessionId !== null && (
        <Tarjeta
          tono="focal"
          titulo="Tienes una sesión a medias"
          acciones={
            <Boton onClick={() => navigate(`/entrenar/sesion/${enCurso.sessionId}/activa`)}>
              Continuar
            </Boton>
          }
        />
      )}

      {sesionDeHoy ? (
        <TarjetaSesion
          titulo="Hoy"
          nombre={sesionDeHoy.name}
          objetivo={sesionDeHoy.objective}
          minutos={sesionDeHoy.estimatedMinutes}
          estado={sesionDeHoy.status}
          onAbrir={() => navigate(`/entrenar/sesion/${sesionDeHoy.id}`)}
        />
      ) : siguiente ? (
        <TarjetaSesion
          titulo={`Próxima · ${formatShortDate(siguiente.scheduledDate)}`}
          nombre={siguiente.name}
          objetivo={siguiente.objective}
          minutos={siguiente.estimatedMinutes}
          estado={siguiente.status}
          onAbrir={() => navigate(`/entrenar/sesion/${siguiente.id}`)}
        />
      ) : atrasadas.length > 0 && atrasadas[0] !== undefined ? (
        <TarjetaSesion
          titulo={
            atrasadas.length === 1
              ? 'Te queda una sesión de esta semana'
              : `Te quedan ${atrasadas.length} sesiones de esta semana`
          }
          nombre={atrasadas[0].name}
          objetivo={atrasadas[0].objective}
          minutos={atrasadas[0].estimatedMinutes}
          estado={atrasadas[0].status}
          onAbrir={() => navigate(`/entrenar/sesion/${atrasadas[0]?.id ?? ''}`)}
        />
      ) : (
        <Tarjeta
          titulo="Hoy toca descansar"
          /*
            Hasta el 13/09/2026 la semana siguiente solo se construia al llegar
            el lunes (ADR-025), y esta frase lo decia. Ahora se prepara en cuanto
            esta termina —`prepareNextWeekIfFinished`, en el efecto de arriba— y
            se rehace si luego se aplica un cambio que la habria construido
            distinta. Esta tarjeta queda para el instante antes de que el efecto
            termine y para la ultima semana de un bloque.
          */
          descripcion="No queda ninguna sesión pendiente esta semana. La siguiente se prepara en Entrenar en cuanto esta termina."
        />
      )}

      <Seccion
        titulo="Cardio"
        descripcion="Lo prescrito para esta semana y el registro de tus carreras."
        acciones={
          <BotonEnlace a="/entrenar/cardio" variante="secundario">
            Ver cardio
          </BotonEnlace>
        }
      />

      <Seccion titulo="Esta semana">
        <div className="lista-ejercicios">
        {sesiones.map((sesion) => (
          <Link
            key={sesion.id}
            to={`/entrenar/sesion/${sesion.id}`}
            className="ejercicio-fila"
          >
            <span className="ejercicio-fila__numero">{sesion.orderInWeek + 1}</span>
            <span className="ejercicio-fila__nombre">
              {sesion.name}
              <span className="ejercicio-fila__musculo"> · {formatShortDate(sesion.scheduledDate)}</span>
            </span>
            <EstadoSesion estado={sesion.status} />
          </Link>
        ))}
        </div>
        {/* Solo la semana que ya ha empezado se puede cerrar: una preparada de
            antemano todavía no se ha vivido. */}
        {semanaActiva !== undefined && semanaActiva.weekStart <= hoy && (
          <CerrarSemana sesiones={sesiones} onCerrada={setAvisoSemana} />
        )}
      </Seccion>
    </>
  );
}

function EstadoSesion({ estado }: { estado: string }): ReactNode {
  if (estado === 'completed') return <Chip>Hecha</Chip>;
  if (estado === 'in_progress') return <Chip tono="aviso">En curso</Chip>;
  if (estado === 'missed') return <Chip tono="error">Perdida</Chip>;
  if (estado === 'skipped') return <Chip tono="neutro">No se hará</Chip>;
  return <Chip tono="neutro">Pendiente</Chip>;
}

function TarjetaSesion({
  titulo,
  nombre,
  objetivo,
  minutos,
  estado,
  onAbrir,
}: {
  titulo: string;
  nombre: string;
  objetivo: string;
  minutos: number;
  estado: string;
  onAbrir: () => void;
}): ReactNode {
  return (
    <Tarjeta tono="focal" className="entreno">
      {/* «Hoy · Torso B» iba entero en el titulo, asi que «Hoy» —que es el
          contexto— pesaba lo mismo que el nombre de la sesion, que es el dato.
          Separados, se lee primero cuando y luego que. */}
      <h2 className="entreno__rotulo t-etiqueta">{titulo}</h2>
      <p className="entreno__nombre">{nombre}</p>
      <p className="entreno__meta">{objetivo}</p>
      <p className="entreno__meta">Duración aproximada: {minutos} min</p>
      <Boton className="entreno__accion" ancho="completo" onClick={onAbrir}>
        {estado === 'completed' ? 'Ver sesión' : 'Ver entrenamiento'}
      </Boton>
    </Tarjeta>
  );
}
